import styled, { WithTheme } from '@emotion/styled';
import { SerializedStyles } from '@emotion/serialize';
import { Theme, StyledProps } from './Tabs.types';
import { space, variant } from 'styled-system';
import css from '@styled-system/css';

const baseActiveAfter = ({ state, tabWidthPerc }: WithTheme<StyledProps, Theme>): SerializedStyles =>
  state === 'active' &&
  css({
    '&::after': {
      content: `''`,
      display: 'block',
      margin: '0 auto',
      width: `${tabWidthPerc}%`,
      height: '4px',
      top: '100%',
      position: 'absolute',
      borderRadius: 25,
    },
  });

const Tab = styled.button<StyledProps, Theme>`
  ${space}

  ${css({
    background: 'none',
    border: 'none',
    outline: 'none',
    position: 'relative',
    fontFamily: 'secondary',
    cursor: 'pointer',
    fontWeight: 400,

    '&:not(:last-child)': {
      marginRight: 3,
    },
  })}

  ${(props) =>
    // Scroll hides the ::after line without a margin
    props.scrollable &&
    css({
      marginBottom: '4px',
    })}

  ${baseActiveAfter}

  ${(props) =>
    props.state === 'active' &&
    variant({
      prop: 'mode',
      variants: {
        primary: {
          color: 'var(--color-coral)',
          fontWeight: 700,
          '&::after': {
            bg: 'var(--color-coral)',
          },
        },
        secondary: {
          color: 'var(--color-vibrant-blue-t8)',
          fontWeight: 700,
          '&::after': {
            bg: 'var(--color-vibrant-blue-t8)',
          },
        },
      },
    })}

  ${(props) =>
    props.state === 'normal' &&
    variant({
      prop: 'mode',
      variants: {
        primary: {
          color: 'var(--color-indigo-t5)',
          '&:hover': {
            background: 'none',
          },
          '&:hover:not(:disabled)': {
            color: 'var(--color-coral-70)',
          },
          '&:active': {
            background: 'none',
            color: 'var(--color-indigo-t11)',
          },
          '&:disabled': {
            background: 'none',
            color: 'var(--color-indigo-t2)',
          },
        },
        secondary: {
          color: 'var(--color-indigo-t11)',
          '&:hover': {
            background: 'none',
          },
          '&:hover:not(:disabled)': {
            color: 'var(--color-vibrant-blue-t6)',
          },
          '&:active': {
            background: 'none',
            color: 'var(--color-indigo-t11)',
          },
          '&:disabled': {
            background: 'none',
            color: 'var(--color-vibrant-blue-t2)',
          },
        },
      },
    })}
`;

export default Tab;
